import { Component, OnInit, Inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { AuthService } from '../../services/auth.service';
import { ToastService } from 'src/app/modules/core/services/toast.service';
import { Subject, takeUntil, finalize } from 'rxjs';

@Component({
  selector: 'app-sso-callback',
  templateUrl: './sso-callback.component.html',
  styleUrls: ['./sso-callback.component.scss'],
  standalone: false
})
export class SsoCallbackComponent implements OnInit {

  private destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private toastService: ToastService,
        @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
    ) {}

    async ngOnInit(): Promise<void> {
        try {            
            await this.handleOktaCallback();
        } catch (error: any) {
            console.error('SSO callback error:', error);
            this.toastService.error(`Authentication failed: ${error?.message || 'Unknown error'}`);
            this.router.navigate(['/account/login']);
        }
    }

    private async handleOktaCallback(): Promise<void> {
    // Handle the redirect
    await this.oktaAuth.handleLoginRedirect();

    // Verify authentication
    const isAuthenticated = await this.oktaAuth.isAuthenticated();
    if (!isAuthenticated) {
        throw new Error('Okta authentication failed');
    }

    // Get ID token
    const idToken = await this.oktaAuth.getIdToken();
      if (!idToken) {
          throw new Error('No ID token received from Okta');
      }

      // Exchange token with backend
      this.authService.callSts(idToken)
          .pipe(
              takeUntil(this.destroy$)
          )
          .subscribe({
              next: (response) => {
                  const token = response?.result?.access_token;
                  
                  if (!token) {
                      throw new Error('No access token received from backend');
                  }

                  // Store token and WAIT for user to load before redirecting
                  this.authService.remoteLoginV2(token, 86400)
                      .pipe(takeUntil(this.destroy$))
                      .subscribe({
                          next: async (user) => {
                              
                              if (!user) {
                                  throw new Error('Failed to load user data');
                              }

                              // Get original destination
                              const originalUri = this.oktaAuth.getOriginalUri() || '/';

                              // Navigate to destination
                              await this.router.navigateByUrl(originalUri);
                          },
                          error: (error) => {
                              console.error('Failed to load user:', error);
                              this.toastService.error('Failed to load user data');
                              throw error;
                          }
                      });
              },
              error: async (error) => {
                  console.error('Token exchange failed:', error);
                  await this.oktaAuth.revokeAccessToken();
                  await this.oktaAuth.revokeRefreshToken();
                  this.oktaAuth.tokenManager.clear();
                  this.toastService.error('Token exchange failed');
                  this.router.navigate(['/account/login']);
              }
          });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

}